import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { ContactAndTravelerSelectors } from "@apac/checkout";
import { IconNameEnum } from "@b2bportal/core-themes";
import { FLIGHTS_PATH } from "@b2bportal/core-utilities";
import {
  PassengerErrorModalTypes,
  useCheckoutState,
  useCheckoutStateSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ErrorModal } from "@hsbc/portal";
import { NotificationBanner } from "@tenant/ui";
import { CheckoutEvent } from "../../events";

export const ConnectedPassengerError = () => {
  const { t } = useI18nContext();
  const navigate = useNavigate();

  const [, send] = useCheckoutState();

  const openErrorModal = useCheckoutStateSelector(
    ContactAndTravelerSelectors.getTravelerErrorOpen
  );
  const passengerError = useCheckoutStateSelector(
    ContactAndTravelerSelectors.getTravelerError
  );

  const isPassportError = useMemo(
    () =>
      (passengerError?.type as unknown as PassengerErrorModalTypes) ===
      PassengerErrorModalTypes.PassportValidationFail,
    [passengerError]
  );

  const clearError = useCallback(
    () => send(CheckoutEvent.CLEAR_TRAVELER_INFORMATION_ERROR),
    [send]
  );

  const searchAgain = useCallback(() => {
    clearError();
    navigate(FLIGHTS_PATH);
  }, [clearError, navigate]);

  useEffect(() => {
    if (isPassportError) {
      send(CheckoutEvent.OPEN_PASSPORT_FORM);
    }
  }, [isPassportError, passengerError, send]);

  // Render error modal when generic error is prompted and user is required to re-enter checkout flow
  // Render error banner when user is not required to exit passenger select screen
  const getErrorModalProps = useMemo(
    () =>
      (
        type?: PassengerErrorModalTypes
      ): {
        showInModal?: boolean;
        title: string;
        subtitle: string;
        primaryButtonText: string;
        primaryOnClick: () => void;
      } => {
        switch (type) {
          case PassengerErrorModalTypes.TripPricing:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PassengerErrorModalTypes.UpdatePassenger:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PassengerErrorModalTypes.FetchPassengers:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PassengerErrorModalTypes.ValidatePassengers:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PassengerErrorModalTypes.NoPassengers:
            return {
              title: t("checkoutError.invalidPassenger"),
              subtitle: t("checkoutError.noPassengersSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.DeletePassenger:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PassengerErrorModalTypes.ValidateAndTripPricing:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PassengerErrorModalTypes.ADTRequired:
            return {
              title: t("checkoutError.noAdultPassengerTitle"),
              subtitle: t("checkoutError.noAdultPassengerSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.INSNotAllowed:
            return {
              title: t("checkoutError.seatedInfantsUnsupportedTitle"),
              subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.InSufficientADTCount:
            return {
              title: t("checkoutError.inSufficientADTTitle"),
              subtitle: t("checkoutError.inSufficientADTSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.UnaccompaniedMinor:
            return {
              title: t("checkoutError.tooManyLapInfantsTitle"),
              subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.TooManyPassengers:
            return {
              title: t("checkoutError.tooManyPassengersTitle"),
              subtitle: t("checkoutError.tooManyPassengersSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.InfantIsMinor:
            return {
              title: t("checkoutError.lapInfantTooOldTitle"),
              subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
              primaryButtonText: t("checkoutError.updateTraveler"),
              primaryOnClick: undefined,
            };
          case PassengerErrorModalTypes.SearchPassengerNumNotReached:
            return {
              title: t("checkoutError.searchPassengerNumNotReachedTitle"),
              subtitle: t("checkoutError.searchPassengerNumNotReachedSubtitle"),
              primaryButtonText: t(
                "checkoutError.searchPassengerNumNotReachedCta"
              ),
              primaryOnClick: undefined,
            };
          default:
            return {
              showInModal: true,
              title: t("checkoutError.genericErrorTitle"),
              subtitle: t("checkoutError.genericErrorSubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
        }
      },
    [searchAgain, t]
  );

  const { showInModal, ...errorModalProps } = useMemo(
    () =>
      getErrorModalProps(
        passengerError?.type as unknown as PassengerErrorModalTypes
      ),

    [passengerError, getErrorModalProps]
  );

  if (!openErrorModal || isPassportError) {
    return null;
  }

  return showInModal ? (
    <ErrorModal
      className="passenger-info-error-modal"
      open={!!openErrorModal}
      {...errorModalProps}
    />
  ) : (
    <NotificationBanner
      onClose={
        // Pass null when there's no onClick function for the error so that
        // the [X] button doesn't appear in the banner
        errorModalProps.primaryOnClick
          ? () => errorModalProps.primaryOnClick()
          : null
      }
      iconName={IconNameEnum.warningCircle}
      {...errorModalProps}
    />
  );
};
